import { useEffect, useRef } from "react";

/**
 * Fires `onAbandoned` once on unmount, or on `pagehide` when the page is not
 * headed for bfcache, so a funnel can report drop-off without every exit path
 * calling it explicitly.
 *
 * Both can fire in the same teardown, so `onAbandoned` must guard its own
 * preconditions (already converted, already reported, nothing to report).
 */
export const useAbandonOnUnmount = (onAbandoned: () => void) => {
	const onAbandonedRef = useRef(onAbandoned);
	onAbandonedRef.current = onAbandoned;

	const pendingAbandonRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		// StrictMode's dev-only unmount/remount is synchronous, so a remount
		// always beats the deferred abandon queued by the cleanup below.
		if (pendingAbandonRef.current !== null) {
			clearTimeout(pendingAbandonRef.current);
			pendingAbandonRef.current = null;
		}
		const handlePageHide = (event: PageTransitionEvent) => {
			if (event.persisted) return;
			onAbandonedRef.current();
		};
		window.addEventListener("pagehide", handlePageHide);
		return () => {
			window.removeEventListener("pagehide", handlePageHide);
			pendingAbandonRef.current = setTimeout(() => {
				pendingAbandonRef.current = null;
				onAbandonedRef.current();
			});
		};
	}, []);
};
